/**
 * A 3D plan's draw state (`spec-onboarding-3d-plan-renderer-switch`, R2.1): what `PlanMapHost`
 * reports and every pane's `usePlanDrawState` holds. In a module of its own so a suite that fakes the
 * host still has the bound and the warning.
 */

/**
 * Why a 3D plan could not be drawn (renderer-switch R2.1). `load`: the 3D renderer's own code could
 * not be loaded (`konva-removal` R4.2); it is set by the providers, never by a host.
 */
export type PlanDrawCause = 'no-token' | 'webgl' | 'map-error' | 'timeout' | 'load';
/** Where a 3D plan's drawing stands, as the host tells it. */
export type PlanDrawState =
  | { kind: 'drawing' }
  | { kind: 'drawn' }
  | { kind: 'failed'; cause: PlanDrawCause };

/**
 * R2.1: how long a plan may take to draw, from its host's mount, before it is said to have failed.
 * The panes' own wait for the twin record runs on the same bound (`usePlanDrawState`).
 */
export const PLAN_DRAW_TIMEOUT_MS = 20_000;
/** mapbox-gl's message when the browser will not give it a WebGL context. */
export const PLAN_WEBGL_FAILED_MESSAGE = 'Failed to initialize WebGL.';

const warnedDrawFailed = new Set<PlanDrawCause>();
/** R2.6: each cause is warned once per page, by its name only: never the token. */
export const warnPlanDrawFailed = (cause: PlanDrawCause) => {
  if (warnedDrawFailed.has(cause)) return;
  warnedDrawFailed.add(cause);
  console.warn('[PlanMapHost] could not draw', cause);
};
